<script lang="ts">
  /**
   * A card the server puts over the game, which the player cannot wave away:
   * a pause ("Waiting for Sam…", with the grace left), the 3-2-1 before play
   * resumes, a match's end before its results. A non-dismissible `Sheet` in
   * its dialog shape: no close button, and Back, Escape and the backdrop
   * call `onbackattempt` with what they were (the game may ask "Leave the
   * match?"); Back is re-armed at once, so it never leaves the app from
   * under the card. The game takes it down by setting `open` to false, when
   * the server says so.
   *
   * Top to bottom: `eyebrow`, `title` (also the dialog's name), a big
   * `figure` (the countdown's number, the grace's seconds; read out
   * politely as it changes), `detail`, the game's own `children`, and up to
   * two buttons (`primary`, `secondary`: Resume now, Leave the match). At
   * least one of them is required: a card the player cannot wave away still
   * offers a way out (the game's "Leave the match?" question).
   *
   * Read out: the title names the dialog and `detail` describes it. The
   * figure is a polite live region that speaks only at `announceAt` (its
   * values, by default the last three seconds and every whole minute), not
   * at every tick of a grace countdown; a figure that is not a countdown
   * (a score) is spoken whenever it changes. `announce` replaces the rule.
   *
   * Test ids: `testid` (default `ui-server-overlay`) on the dialog,
   * `testids.title` (default `ui-server-overlay-title`, since 1.3.0) on the
   * title, `figureTestid` (default `ui-server-overlay-figure`); the buttons
   * take their own.
   */
  import type { Snippet } from 'svelte';

  import type { BackAttemptHandler } from '../templates/playTypes.js';
  import type { TemplateAction } from '../types.js';
  import PillButton from './PillButton.svelte';
  import Sheet from './Sheet.svelte';

  interface CardProps {
    readonly open: boolean;
    readonly title: string;
    readonly eyebrow?: string | undefined;
    readonly figure?: string | number | undefined;
    /** Whether `figure` is spoken now (see "Read out"). */
    readonly announce?: ((figure: string | number) => boolean) | undefined;
    readonly detail?: string | undefined;
    readonly children?: Snippet | undefined;
    readonly onbackattempt?: BackAttemptHandler | undefined;
    readonly testid?: string | undefined;
    readonly figureTestid?: string | undefined;
    /** `title`: on the title (the `<h2>`), default `ui-server-overlay-title`. Since 1.3.0. */
    readonly testids?: { readonly title?: string } | undefined;
  }

  /** A way out, always: `primary`, `secondary` or both. */
  type Actions =
    | { readonly primary: TemplateAction; readonly secondary?: TemplateAction | undefined }
    | { readonly primary?: TemplateAction | undefined; readonly secondary: TemplateAction };

  type Props = CardProps & Actions;
  const {
    open,
    title,
    eyebrow,
    figure,
    announce = announceByDefault,
    detail,
    children,
    primary,
    secondary,
    onbackattempt,
    testid = 'ui-server-overlay',
    figureTestid = 'ui-server-overlay-figure',
    testids,
  }: Props = $props();

  const headingId = $props.id();
  const detailId = `${headingId}-detail`;

  /**
   * A countdown is spoken in its last three seconds and on whole minutes
   * ("0:03", "3", "1:00"); anything that is not a clock reading is spoken.
   */
  function announceByDefault(value: string | number): boolean {
    const text = String(value).trim();
    const clock = /^(?:(\d+):)?(\d{1,2})$/.exec(text);
    if (clock === null) return true;
    const seconds = Number(clock[1] ?? 0) * 60 + Number(clock[2]);
    return seconds <= 3 || seconds % 60 === 0;
  }

  // The live region beside the figure: it takes the figure's value only when
  // that should be heard, and otherwise keeps what it last said (unchanged
  // text is not read again). The figure itself is ordinary text.
  let spoken = $state<string>('');
  $effect.pre(() => {
    if (figure !== undefined && announce(figure)) spoken = String(figure);
  });
</script>

{#snippet titleSnippet()}
  <div class="ui-server-head">
    {#if eyebrow !== undefined}<p class="ui-server-eyebrow">{eyebrow}</p>{/if}
    <h2
      class="ui-server-title"
      id={headingId}
      data-testid={testids?.title ?? 'ui-server-overlay-title'}
    >
      {title}
    </h2>
  </div>
{/snippet}

<Sheet
  {open}
  {title}
  variant="dialog"
  role="alertdialog"
  dismissible={false}
  {onbackattempt}
  onclose={() => {
    // Never dismissible: every way out is an `onbackattempt`.
  }}
  header={titleSnippet}
  labelledby={headingId}
  describedby={detail === undefined ? undefined : detailId}
  gap="md"
  {testid}
>
  <div class="ui-server-body">
    {#if figure !== undefined}
      <p class="ui-server-figure" data-testid={figureTestid}>{figure}</p>
      <!-- Only what should be heard: a grace countdown is not read out every second. -->
      <p class="ui-server-spoken" aria-live="polite" data-testid="{figureTestid}-spoken">
        {spoken}
      </p>
    {/if}
    {#if detail !== undefined}<p class="ui-server-detail" id={detailId}>{detail}</p>{/if}
    {#if children !== undefined}{@render children()}{/if}
    {#if primary !== undefined || secondary !== undefined}
      <div class="ui-server-actions">
        {#if primary !== undefined}
          <PillButton
            label={primary.label}
            onclick={primary.onclick}
            disabled={primary.disabled ?? false}
            busy={primary.busy ?? false}
            icon={primary.icon}
            autofocus
            testid={primary.testid}
          />
        {/if}
        {#if secondary !== undefined}
          <PillButton
            label={secondary.label}
            onclick={secondary.onclick}
            variant="plain"
            tone="danger"
            disabled={secondary.disabled ?? false}
            busy={secondary.busy ?? false}
            icon={secondary.icon}
            testid={secondary.testid}
          />
        {/if}
      </div>
    {/if}
  </div>
</Sheet>

<style>
  .ui-server-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 0 0 0.75rem;
    text-align: center;
  }

  .ui-server-eyebrow {
    margin: 0;
    font: var(--ui-text-eyebrow);
    letter-spacing: var(--ui-tracking-eyebrow);
    text-transform: uppercase;
    color: var(--ui-ink-2);
  }

  .ui-server-title {
    margin: 0;
    font: var(--ui-text-heading);
    color: var(--ui-ink);
  }

  .ui-server-body {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    text-align: center;
    color: var(--ui-ink);
    font-family: var(--ui-font);
  }

  .ui-server-figure {
    margin: 0;
    font: var(--ui-text-display);
    font-variant-numeric: tabular-nums;
    color: var(--ui-accent);
  }

  .ui-server-spoken {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .ui-server-detail {
    margin: 0;
    font: var(--ui-text-body);
    color: var(--ui-ink-2);
  }

  .ui-server-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
</style>
